/**
 * @author Antoine Laviron
 * @date 2024-08-27
 *
 * @description
 * This component is responsible for embedding a specific, hardcoded Salesforce
 * Analytics dashboard ('Sales_Cloud_Dashboard'). It handles authentication checks,
 * dynamically loads the Analytics Embedding SDK, and manages the rendering
 * process into a designated container within the component's DOM.
 */

import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

function SalesCloud() {
  // Hook for programmatic navigation (e.g., redirecting to login).
  const navigate = useNavigate();
  // A ref used as a flag to prevent the SDK initialization from running more than once.
  // Unlike state, updating a ref does not trigger a re-render.
  const sdkInitialized = useRef(false);
  // A ref to create a direct reference to the dashboard's container div in the DOM.
  // This allows the SDK to know exactly where to render the dashboard.
  const dashboardContainerRef = useRef(null); // Ref to the container div

  useEffect(() => {
    // --- Authentication Check ---
    // Retrieve the frontdoor URL and instance URL from local storage.
    // These are required to authenticate with the Analytics Embedding SDK.
    const frontdoorUrl = localStorage.getItem("frontdoor_url");
    const instanceUrl = localStorage.getItem("instance_url");

    // If either credential is not found, the user is considered unauthenticated.
    // Redirect them to the login page.
    if (!frontdoorUrl || !instanceUrl) {
      navigate("/");
      return;
    }

    // --- Initialization Guard ---
    // This check ensures the embedding logic only runs once. It verifies that the
    // sdkInitialized flag is false and that the container element has been rendered.
    if (sdkInitialized.current || !dashboardContainerRef.current) {
      return;
    }
    // Set the flag to true immediately to prevent this block from re-entering
    // during subsequent renders (e.g., in React's StrictMode).
    sdkInitialized.current = true;

    // --- SDK Loading and Dashboard Embedding ---
    // Dynamically import the Analytics Embedding SDK from the npm package.
    import("@salesforce/analytics-embedding-sdk")
      .then(({ initializeAnalyticsSdk, AnalyticsDashboard }) => {
        // Prepare the configuration object for the SDK.
        const config = {
          authCredential: frontdoorUrl,
          orgUrl: instanceUrl,
        };
        // Initialize the SDK with the provided credentials.
        initializeAnalyticsSdk(config).then(() => {
          // Once the SDK is ready, create a new instance of the dashboard component.
          const dashboard = new AnalyticsDashboard({
            // Pass the actual DOM element reference as the parent container.
            parentIdOrElement: dashboardContainerRef.current,
            // Specify the developer API name of the dashboard to embed.
            idOrApiName: "Sales_Cloud_Dashboard",
          });
          // Render the dashboard into the container.
          dashboard.render();
        });
      })
      .catch((error) => {
        console.error("Failed to load or initialize the Analytics SDK:", error);
      });
  }, [navigate]);

  /**
   * Clears user session data from local storage and navigates to the login page.
   */
  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  return (
    <div className="dashboard-page">
      <header className="page-header">
        <h1>Sales Cloud</h1>
        <button onClick={handleLogout} className="logout-button-styled">
          Logout
        </button>
      </header>

      {/* Salesforce Dashboard embedded */}
      <div
        className="dashboard-embedding-container"
        ref={dashboardContainerRef}
      ></div>
    </div>
  );
}

export default SalesCloud;
